import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Dropdown, Menu, MenuProps, Tooltip } from "antd";
import { 
  QuestionOutlined, 
  LogoutOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  ArrowRightOutlined
} from "@ant-design/icons";
import { ReactComponent as AwardIcon } from "../../assets/icon/award.svg";
import { ReactComponent as BookOpenIcon } from "../../assets/icon/book-open-1.svg";
import { ReactComponent as BriefcaseIcon } from "../../assets/icon/briefcase-1.svg";
import { ReactComponent as DesajustIcon } from "../../assets/icon/desajust.svg";
// import { ReactComponent as BrainIcon } from "../../assets/icon/brain.svg";

import styles from "./Aside.module.css";
import { PATH_ROUTES } from "../../router/routes";
import { useAuthContext, useCompare } from "../../providers";
import InformeUnificatModal, { type InformeStatus } from "../../components/Modals/InformeUnificat/InformeUnificatModal";
import textsFetcher from "../../services/api/TextsFetch";
import { captureAllCharts } from "../../utils/captureCharts";
import { MouseEventHandler } from "react";

interface AsideProps {
  onCollapseChange?: (isCollapsed: boolean) => void;
  forceCollapsed?: boolean;
}

const helpItems: MenuProps["items"] = [
  {
    key: "1",
    label: <a href="mailto:dsuarez@omnios.ai">Contacta amb el suport</a>,
  },
  {
    type: "divider",
  },
  {
    key: "2",
    label: (
      <a
        className={styles.omniosLink}
        href="https://omnios.ai/"
        target="_blank"
        rel="noreferrer"
      >
        © {new Date().getFullYear()} • Omnios
      </a>
    ),
  },
];

const modulesItems: MenuProps["items"] = [
  {
    key: "titulacions",
    label: "Tendències per àmbits d'estudi",
    icon: (
      <span className="anticon">
        <AwardIcon />
      </span>
    ),
  },
  {
    key: "professions-regulades",
    label: "Presència de professionals per sector",
    icon: (
      <span className="anticon">
        <BriefcaseIcon />
      </span>
    ),
  },
  // Temporalment oculta del menú principal.
  // {
  //   key: "professionals-sanitaris",
  //   label: "Projecció del desajust de Professionals Sanitaris",
  //   icon: (
  //     <span className="anticon">
  //       <BrainIcon />
  //     </span>
  //   ),
  // },
  {
    key: "desajust-laboral-titulacio",
    label: "Desajust laboral per titulació",
    icon: (
      <span className="anticon">
        <BookOpenIcon />
      </span>
    ),
  },
  {
    key: "oferta-universitaria-i-ocupacio-sectorial",
    label: "Oferta universitària i ocupació sectorial",
    icon: (
      <span className="anticon">
        <DesajustIcon />
      </span>
    ),
  },
];

const actionsItems: MenuProps["items"] = [
  {
    key: "informe-unificat",
    label: "Informe unificat",
    icon: <DownloadOutlined />,
  },
];

const MODULE_TO_PATH: Record<string, string> = {
  "titulacions": PATH_ROUTES.titulacions,
  "professions-regulades": PATH_ROUTES.professionsRegulades,
  "professionals-sanitaris": PATH_ROUTES.professionalsSanitaris,
  "desajust-laboral-titulacio": PATH_ROUTES.desajustLaboralTitulacio,
  "oferta-universitaria-i-ocupacio-sectorial": PATH_ROUTES.ofertaUniversitariaOcupacio,
};

const Aside = ({ onCollapseChange, forceCollapsed = false }: AsideProps) => {
  const { logout } = useAuthContext();
  const { toggleCompare } = useCompare();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const effectivelyCollapsed = isCollapsed || forceCollapsed;
  const [informeUnificatModalOpen, setInformeUnificatModalOpen] = useState(false);
  const [informeStatus, setInformeStatus] = useState<InformeStatus>("idle");
  const navigate = useNavigate();
  const location = useLocation();

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === PATH_ROUTES.professionsRegulades) return ["professions-regulades"];
    if (path === PATH_ROUTES.professionalsSanitaris) return ["professionals-sanitaris"];
    if (path === PATH_ROUTES.desajustLaboralTitulacio) return ["desajust-laboral-titulacio"];
    if (path === PATH_ROUTES.ofertaUniversitariaOcupacio) {
      return ["oferta-universitaria-i-ocupacio-sectorial"];
    }
    if (path === "/oferta-universitaria") return ["desajust-laboral-titulacio"];
    if (path === "/desajust-titulacio") {
      return ["oferta-universitaria-i-ocupacio-sectorial"];
    }
    return ["titulacions"];
  };

  const onSignOutClick: MouseEventHandler<HTMLButtonElement> = () => {
    logout();
  };

  const handleModuleClick = (key: string) => {
    const path = MODULE_TO_PATH[key];
    if (path) navigate(path);
  };

  const handleActionClick = (key: string) => {
    if (key === "informe-unificat") {
      setInformeUnificatModalOpen(true);
    }
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
  };

  return (
    <aside className={`${styles.aside} ${effectivelyCollapsed ? styles.collapsed : ""}`}>
      <div className={styles.header}>
        <h2 className={styles.appTitle}>EstudIA</h2>
        <Button
          type="text"
          className={styles.collapseButton}
          icon={forceCollapsed ? <ArrowRightOutlined /> : isCollapsed ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
          onClick={forceCollapsed ? () => {
            toggleCompare();
            setIsCollapsed(false);
            onCollapseChange?.(false);
          } : toggleCollapse}
        />
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>MÒDULS</h3>
        <Menu
          mode="inline"
          className={styles.menu}
          selectedKeys={getSelectedKeys()}
          items={modulesItems}
          onClick={(info) => handleModuleClick(info.key)}
        />
      </div>

      <div className={styles.divider}></div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>ACCIONS</h3>
        <Menu
          mode="inline"
          className={`${styles.menu} ${styles.menuActions}`}
          selectedKeys={informeUnificatModalOpen ? ["informe-unificat"] : []}
          items={actionsItems}
          onClick={(info) => handleActionClick(info.key)}
        />
      </div>

      <div className={styles.footer}>
        <Tooltip title="Sortir" placement="right">
          <Button className={styles.signOutButton} size="small" icon={<LogoutOutlined />} onClick={onSignOutClick}/>
        </Tooltip>
        <Dropdown placement="topLeft" menu={{ items: helpItems }} trigger={["click"]}>
          <Tooltip title="Suport" placement="right">
            <Button size="small" icon={<QuestionOutlined />} />
          </Tooltip>
        </Dropdown>
      </div>

      <InformeUnificatModal
        open={informeUnificatModalOpen}
        status={informeStatus}
        onCancel={() => {
          setInformeUnificatModalOpen(false);
          setInformeStatus("idle");
        }}
        onDownload={async () => {
          setInformeStatus("loading");
          try {
            const chartImages = await captureAllCharts();
            await textsFetcher.downloadReport(chartImages);
            setInformeStatus("success");
          } catch {
            setInformeStatus("error");
          }
        }}
      />
    </aside>
  );
};
export default Aside;
